import { useMemo } from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { format, parseISO } from "date-fns";
import AreaChart from "../../../../components/Charts/AreaChart";
import { GLOBAL_REPORT_PLATFORM_COLORS } from "../../../GlobalReport/utils/globalReportChartPalette";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  OWNER_AD_SPEND_CHART_DAILY,
  OWNER_AD_SPEND_CHART_MONTHLY,
  OWNER_AD_SPEND_CHART_TITLE,
} from "../../../../i18n/keysTranslations";
import { PLATFORMS_SUPPORTED } from "../../../../utils/constants";
import { getDateFnsLocale } from "../../../../utils/i18nLanguage";
import {
  AD_SPEND_CHART_TOOLTIP_SX,
  formatAdSpendAxis,
  formatAdSpendPlatform,
  formatAdSpendUsd,
} from "./adSpendDisplay";

const AdSpendTrendChart = ({
  chartData = [],
  granularity = "monthly",
  isLoading = false,
}) => {
  const { t, i18n } = useTranslationApp();
  const language = i18n.language;
  const locale = getDateFnsLocale(language);
  const isMonthly = granularity === "monthly";

  const localizedData = useMemo(
    () =>
      chartData.map((row) => {
        const dateKey = isMonthly ? `${row.key}-01` : row.key;
        return {
          ...row,
          label: format(parseISO(dateKey), isMonthly ? "MMM yyyy" : "d MMM", {
            locale,
          }),
        };
      }),
    [chartData, isMonthly, locale]
  );

  const chartConfig = useMemo(() => {
    const config = {};
    PLATFORMS_SUPPORTED.forEach((platform) => {
      config[platform] = {
        label: formatAdSpendPlatform({ platform, t }),
        color: GLOBAL_REPORT_PLATFORM_COLORS[platform],
        valueFormatter: (value) => formatAdSpendUsd({ value, language }),
      };
    });
    return config;
  }, [language, t]);

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={320}
        sx={{ borderRadius: 2, width: "100%" }}
      />
    );
  }

  return (
    <Box sx={{ width: "100%", ...AD_SPEND_CHART_TOOLTIP_SX }}>
      <AreaChart
        stacked
        height={320}
        xAxisKey="label"
        title={t(OWNER_AD_SPEND_CHART_TITLE)}
        subtitle={t(
          isMonthly ? OWNER_AD_SPEND_CHART_MONTHLY : OWNER_AD_SPEND_CHART_DAILY
        )}
        chartData={localizedData}
        chartConfig={chartConfig}
        xAxisInterval="equidistantPreserveStart"
        yAxisTickFormatter={(value) => formatAdSpendAxis({ value, language })}
      />
    </Box>
  );
};

export default AdSpendTrendChart;
